/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-undef */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Autocomplete,
  TextFieldProps,
} from "@mui/material";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { useToaster } from "../../../hooks/useToaster";
import { SEVERITIES } from "../../../data/constants";
import { UserDto } from "../../../types/User";
import useApi from "../../../hooks/useApi";
import { INTERNALMESSAGES } from "../../../constants/messages";
import { getActiveUsersWithPermission, reassignMessage } from "../../../services/messageService";
import { MessagesReassignmentsDto } from "../../../types/Message";
import { getCurrentESTTime } from "../../../utils/dateHelper";
import { useUser } from "../../../context/UserContext";
import BackdropProgressBar from "../../../components/ui/BackdropProgressBar";

interface ReassignMessageProps {
  open: boolean;
  onClose: (success?: boolean) => void;
  messageId: number;
  currentAssigneeId: number;
  recipientId: number;
}

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef<HTMLDivElement>(function ListboxComponent(props, ref) {
  const { children, ...other } = props as any;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 36;

  const height = Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING;

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          height={height}
          itemCount={itemCount}
          itemSize={itemSize}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemData={itemData}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const ReassignMessage: React.FC<ReassignMessageProps> = ({
  open,
  onClose,
  messageId,
  recipientId,
}) => {
  const { executeApi, loading } = useApi();
  const showToaster = useToaster();
  const [eligibleUsers, setEligibleUsers] = useState<UserDto[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [memo, setMemo] = useState<string>("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const { userId } = useUser();
  const hasFetchedRef = React.useRef(false);

  const fetchEligibleUsers = useCallback(async () => {
    const users = await executeApi<void, UserDto[]>(getActiveUsersWithPermission, null);
    setEligibleUsers(users || []);
    setSelectedUser(null);
  }, []);

  useEffect(() => {
    if (open && !hasFetchedRef.current) {
      fetchEligibleUsers();
      setMemo("");
      setValidationError(null);
      hasFetchedRef.current = true;
    }
    if (!open) {
      hasFetchedRef.current = false;
    }
  }, [open]);

  const handleSend = async () => {
    if (!selectedUser) {
      setValidationError(INTERNALMESSAGES.SELECT_REASSIGN_USER);
      return;
    }
    setValidationError(null);

    const messagesReassignmentsDto: MessagesReassignmentsDto = {
      messageId: messageId,
      recipientId: recipientId,
      reassignedByUserId: userId,
      reassignedToUserId: selectedUser.userId,
      toCacheName: selectedUser.userName ?? null,
      reassignedOn: getCurrentESTTime(),
      memo: memo,
    };

    const reassign = await executeApi<MessagesReassignmentsDto, MessagesReassignmentsDto[]>(
      reassignMessage,
      messagesReassignmentsDto,
    );

    if (reassign) {
      showToaster(INTERNALMESSAGES.REASSIGN_SUCCESS, SEVERITIES.SUCCESS);
      onClose(true);
    } else {
      showToaster(INTERNALMESSAGES.REASSIGN_FAIL, SEVERITIES.ERROR);
    }
  };

  return (
    <>
      <Box>
        <BackdropProgressBar loading={loading} />
      </Box>
      <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
        <DialogTitle>Reassign Message</DialogTitle>
        <DialogContent>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              value={selectedUser}
              sx={{ mt: 2, mb: 2 }}
              onChange={(event, newValue) => setSelectedUser(newValue)}
              options={eligibleUsers}
              getOptionLabel={(option) => option.userName}
              renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
                <TextField {...params} label="User" size="small" />
              )}
              ListboxComponent={ListboxComponent as any}
              renderOption={(props, option) => <li {...props}>{option.userName}</li>}
            />

            <TextField
              multiline
              minRows={3}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Enter memo"
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} color="inherit" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            color="primary"
            size="small"
            disabled={loading}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReassignMessage;
